import {defineStore} from 'pinia'
import {CommonNs, IntegrationNs, ProjectNs, ScreenNs} from "@shared/types";
import type {DriveNs} from "@shared/types";
import {IntegrationApi} from "@/api";
import {DriveApi} from "@/api/drive.apis";
import {FigmaApi} from "@/api/figma.apis";
import {toast} from '@shared/ms-components'

function isDriveAuthError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const e = error as Record<string, unknown>
  const response = e.response as Record<string, unknown> | undefined
  const status = response?.status as number | undefined
  if (status === 403) return true
  const data = response?.data as Record<string, unknown> | undefined
  const msg = String(data?.message ?? (e as {message?: string}).message ?? '').toLowerCase()
  return (
    (msg.includes('integration') || msg.includes('google')) &&
    (msg.includes('not active') || msg.includes('expired') || msg.includes('unauthorized') || msg.includes('token'))
  )
}


export const useIntegrationStore = defineStore("integration", {
  state: () => ({
    workspaceIntegrations: [] as IntegrationNs.IntegrationInfo[],
    integrationDetailInfo: {} as IntegrationNs.IntegrationInfo,
    projectIntegration: {} as IntegrationNs.ProjectIntegrationInfo
  }),
  getters: {
    getWorkspaceIntegrations: (state) => state.workspaceIntegrations,
    getIntegrationDetail: (state) => state.integrationDetailInfo,
    getProjectIntegration: (state) => state.projectIntegration,
    isDriveConnected: (state) =>
      state.workspaceIntegrations.some(
        (i) => i.integrationType === IntegrationNs.IntegrationTypeEnums.GOOGLE_DRIVE && i.isActive,
      ),
    isIntegrationActive:
      (state) =>
      (type: IntegrationNs.IntegrationTypeEnums): boolean =>
        state.workspaceIntegrations.some((i) => i.integrationType === type && i.isActive),
  },
  actions: {
    async loadWorkspaceIntegrations(
        payload: IntegrationNs.IntegrationInfoRequest
    ): Promise<IntegrationNs.IntegrationInfo[]> {
      return IntegrationApi.getWorkspaceIntegrations(payload)
      .then((response: IntegrationNs.IntegrationInfo[]) => {
        this.workspaceIntegrations = response;
        return response;
      })
    },

    async loadIntegratedProjects(
        payload: IntegrationNs.IntegrationInfoRequest
    ): Promise<IntegrationNs.IntegrationInfo> {
      return IntegrationApi.getIntegratedProjects(payload)
      .then((response: IntegrationNs.IntegrationInfo) => {
        this.integrationDetailInfo = response;
        return response;
      })
    },

    async removeIntegration(
        payload: IntegrationNs.IntegrationInfoRequest
    ): Promise<IntegrationNs.IntegrationInfo[]> {
      return IntegrationApi.removeIntegration(payload)
      .then((response: IntegrationNs.IntegrationInfo[]) => {
        this.workspaceIntegrations = response;
        this.integrationDetailInfo = {} as IntegrationNs.IntegrationInfo;
        return response
      })
    },

    async removeProjectIntegration(
        payload: IntegrationNs.RemoveIntegrationRequest
    ): Promise<CommonNs.AnyResponse> {
      return IntegrationApi.removeProjectIntegration(payload)
      .then((response: CommonNs.AnyResponse) => response)
    },

    async loadNotIntegratedProjectsInWorkspace(
        payload: IntegrationNs.IntegrationInfoRequest
    ): Promise<ProjectNs.Project[]> {
      return IntegrationApi.getNotIntegratedProjectsInWorkspace(payload)
      .then((response: ProjectNs.Project[]) => response)
    },

    async loadAllProjectsToIntegration(
        payload: IntegrationNs.IntegrationInfoRequest
    ): Promise<ProjectNs.Project[]> {
      return IntegrationApi.getAllProjectsToIntegration(payload)
      .then((response: ProjectNs.Project[]) => response)
    },

    async getAuthorizeUrl(
        payload: IntegrationNs.IntegrationInfoRequest
    ): Promise<string> {
      return IntegrationApi.getAuthorizeUrl(payload)
      .then((response: string) => response)
    },

    async confirmAuthentication(
        payload: IntegrationNs.ConfirmAuthenticationRequest
    ): Promise<CommonNs.AnyResponse> {
      return IntegrationApi.confirmAuthentication(payload)
      .then((response: string) => response)
    },

    async storeAzureCredentials(
        payload: IntegrationNs.StoreAzureCredentialsRequest
    ): Promise<void> {
      return IntegrationApi.storeAzureCredentials(payload)
    },

    async loadIssueTypes(
        payload: IntegrationNs.GetIssueTypeRequest
    ): Promise<IntegrationNs.IntIssueType[]> {
      return IntegrationApi.getIssueTypes(payload)
      .then((response: IntegrationNs.IntIssueType[]) => response)
    },

    async completeIntegration(
        payload: IntegrationNs.ProjectIntegrationRequest
    ): Promise<CommonNs.AnyResponse> {
      return IntegrationApi.completeIntegration(payload)
      .then((response: CommonNs.AnyResponse) => response)
    },

    async reimportIssues(
        payload: IntegrationNs.ProjectIntegrationReimportRequest
    ): Promise<CommonNs.AnyResponse> {
      return IntegrationApi.reimportIssues(payload)
      .then((response: CommonNs.AnyResponse) => response)
    },


    async getFigmaRootNodes(workspaceId: string, fileKey: string): Promise<IntegrationNs.FigmaNode[]> {
      return FigmaApi.getRootNodes({workspaceId, fileKey} as IntegrationNs.BaseFigmaRequest)
      .then((response: IntegrationNs.FigmaNode[]) => response)
    },

    async getFigmaChildNodes(workspaceId: string, fileKey: string, nodeId: string): Promise<IntegrationNs.FigmaNode[]> {
      return FigmaApi.getChildNodes({workspaceId, fileKey, nodeId} as IntegrationNs.GetChildNodesRequest)
      .then((response: IntegrationNs.FigmaNode[]) => response)
    },

    async saveFigmaImageAsScreen(payload: IntegrationNs.FigmaImageUploadRequest): Promise<ScreenNs.Screen[]> {
      return FigmaApi.saveImageAsScreen(payload)
      .then((response: ScreenNs.Screen[]) => {
        return response;
      })
    },

    showDriveReconnectToaster(): void {
      toast.error('Google Drive connection expired. Please reconnect from Settings > Integrations.')
    },

    async listDriveFiles(
        payload: DriveNs.ListFilesRequest
    ): Promise<DriveNs.ListFilesResponse> {
      try {
        return await DriveApi.listFiles(payload)
      } catch (error) {
        if (isDriveAuthError(error)) this.showDriveReconnectToaster()
        throw error
      }
    },

    async importDriveContent(
        payload: DriveNs.ImportContentRequest
    ): Promise<DriveNs.ImportContentResponse> {
      try {
        return await DriveApi.importContent(payload)
      } catch (error) {
        if (isDriveAuthError(error)) this.showDriveReconnectToaster()
        throw error
      }
    },

    async importDriveFileToRag(
        payload: DriveNs.ImportToRagRequest
    ): Promise<DriveNs.IngestionResponse> {
      try {
        return await DriveApi.importToRag(payload)
      } catch (error) {
        if (isDriveAuthError(error)) this.showDriveReconnectToaster()
        throw error
      }
    },

    async attachDriveFileToScreen(
        payload: DriveNs.AttachToScreenRequest
    ): Promise<ScreenNs.Screen[]> {
      try {
        return await DriveApi.attachToScreen(payload)
      } catch (error) {
        if (isDriveAuthError(error)) this.showDriveReconnectToaster()
        throw error
      }
    },

    async exportToDrive(payload: DriveNs.ExportRequest): Promise<DriveNs.ExportResponse> {
      try {
        return await DriveApi.export(payload)
      } catch (error) {
        if (isDriveAuthError(error)) this.showDriveReconnectToaster()
        throw error
      }
    },
  }
})
